import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { ConfirmDialog, Input } from '@bliks-io/ui';

interface ResetSignInPerson {
  firstName: string;
  lastName: string;
  email: string;
}

interface ResetSignInConfirmDialogProps {
  /** The person to reset; the dialog is open while this is set. */
  person: ResetSignInPerson | undefined;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

export function ResetSignInConfirmDialog({
  person,
  onClose,
  onConfirm,
  isPending
}: ResetSignInConfirmDialogProps) {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState('');
  const [shownPerson, setShownPerson] = useState(person);
  if (person !== shownPerson) {
    setShownPerson(person);
    setConfirmInput('');
  }

  const name = person ? `${person.firstName} ${person.lastName}` : '';

  return (
    <ConfirmDialog
      open={person !== undefined}
      onOpenChange={(open) => !open && onClose()}
      icon={<KeyRound className="w-5 h-5" />}
      iconTone="danger"
      title={t('organization.members.resetSignInAction')}
      description={
        person &&
        t('organization.members.resetSignInDescription', {
          name,
          email: person.email
        })
      }
      confirmLabel={t('organization.members.resetSignInAction')}
      onConfirm={onConfirm}
      variant="danger"
      isPending={isPending}
      isConfirmDisabled={confirmInput !== name}
    >
      <p className="text-sm text-muted-foreground mb-3">
        <Trans
          i18nKey="organization.members.resetSignInConfirmInstruction"
          components={{
            strong: <strong className="text-foreground font-semibold">{name}</strong>
          }}
        />
      </p>
      <Input
        value={confirmInput}
        onChange={(e) => setConfirmInput(e.target.value)}
        aria-label={t('organization.members.resetSignInConfirmLabel')}
        autoComplete="off"
      />
    </ConfirmDialog>
  );
}
